import {
    ArrowLeft,
    ArrowLeftRight,
    ArrowRight,
    ArrowUp,
    ArrowUpDown,
    BadgePercent,
    Banknote,
    Bike,
    Car,
    CheckCircle2,
    ClipboardList,
    CreditCard,
    FileText,
    Folder,
    Hammer,
    History,
    IdCard,
    Landmark,
    Laptop,
    LayoutDashboard,
    List,
    Package,
    Pencil,
    Plus,
    QrCode,
    Receipt,
    ReceiptText,
    Scale,
    Settings,
    ShoppingBasket,
    ShoppingCart,
    Store,
    Truck,
    Undo2,
    User,
    Users,
    Wrench,
} from 'lucide-react'
import { AccountInfo } from '../../context/account_context'
import { ConfigsInfo, SystemBranchEnum } from '../../context/configs_context'

export interface Navigation {
    text: string
    validate(account: AccountInfo, configs: ConfigsInfo): boolean
    icon?: JSX.Element
    link?: string
    subitems?: Navigation[]
    reducedText?: string
    description?: string
    hide?: boolean
}

export const navMenu: Navigation[] = [
    {
        text: 'Productos',
        icon: <Package />,
        description: 'Gestiona los productos de tu inventario',
        validate: (account) => {
            return (
                account.isMaster ||
                !!account.permissions?.some((p) => p.startsWith('Produtos:'))
            )
        },
        subitems: [
            {
                text: 'Lista productos',
                link: '/system/product',
                icon: <List />,
                description: 'Consulta y busca los productos registrados',
                reducedText: 'Lista',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Produtos:Listar')
                    )
                },
            },
            {
                text: 'Nuevo producto',
                link: '/system/product/new',
                icon: <Plus />,
                description: 'Registra un nuevo producto',
                reducedText: 'Nuevo',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Produtos:Criar')
                    )
                },
            },
            {
                text: 'Editar producto',
                link: '/system/product/edit',
                description: 'Edita los datos de un producto',
                validate: (account) => {
                    return true
                },
                hide: true,
            },
            {
                text: 'Histórico de productos',
                link: '/system/product/historic',
                icon: <History />,
                description:
                    'Consulta el historial de movimientos de un producto',
                reducedText: 'Histórico',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes(
                            'Produtos:Listar histórico'
                        )
                    )
                },
            },
            {
                text: 'Descuentos',
                link: '/system/product/discount',
                icon: <BadgePercent />,
                description: 'Gestiona los descuentos de productos',
                reducedText: 'Descuentos',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes(
                            'Produtos:Listar descontos'
                        )
                    )
                },
            },
            {
                text: 'Nuevo Descuento',
                link: '/system/product/discount/new',
                icon: <Plus />,
                description: 'Registra un nuevo descuento',
                reducedText: 'Nuevo Descuento',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes(
                            'Produtos:Criar desconto'
                        )
                    )
                },
                hide: true,
            },
            {
                text: 'Cargar stock',
                link: '/system/product/load-stock',
                icon: <ArrowUp />,
                description: 'Gestiona las cargas de stock',
                reducedText: 'Cargar stock',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes(
                            'Produtos:Carregar estoque'
                        )
                    )
                },
            },
            {
                text: 'Nueva carga de stock',
                link: '/system/product/load-stock/new',
                description: 'Registra una nueva carga de stock',
                reducedText: 'Nuevo',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes(
                            'Produtos:Carregar estoque'
                        )
                    )
                },
                hide: true,
            },
            {
                text: 'Transferir stock',
                link: '/system/product/transfer-stock',
                icon: <ArrowLeftRight />,
                description:
                    'Gestiona las transferencias de stock entre sucursales',
                reducedText: 'Transferir stock',
                validate: (account, configs) => {
                    return (
                        (account.isMaster ||
                            !!account.permissions?.includes(
                                'Produtos:Transferir estoque'
                            )) &&
                        configs.systemBranch === SystemBranchEnum.Branch
                    )
                },
            },
            {
                text: 'Nueva transferencia de stock',
                link: '/system/product/transfer-stock/new',
                description: 'Registra una nueva transferencia de stock',
                reducedText: 'Nuevo',
                validate: (account, configs) => {
                    return (
                        (account.isMaster ||
                            !!account.permissions?.includes(
                                'Produtos:Transferir estoque'
                            )) &&
                        configs.systemBranch === SystemBranchEnum.Branch
                    )
                },
                hide: true,
            },
            {
                text: 'Seriales',
                link: '/system/product/load-serial',
                icon: <Laptop />,
                description: 'Gestiona las cargas de serie',
                reducedText: 'Seriales',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes(
                            'Produtos:Carregar serial'
                        )
                    )
                },
            },
            {
                text: 'Nueva carga de serial',
                link: '/system/product/load-serial/new',
                description: 'Registra una nueva carga de serie',
                reducedText: 'Nuevo',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes(
                            'Produtos:Carregar serial'
                        )
                    )
                },
                hide: true,
            },
            {
                text: 'Lotes',
                link: '/system/product/load-lote',
                icon: <Package />,
                description: 'Gestiona las cargas de lote',
                reducedText: 'Lotes',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes(
                            'Produtos:Carregar lote'
                        )
                    )
                },
            },
            {
                text: 'Nueva carga de lote',
                link: '/system/product/load-lote/new',
                description: 'Registra una nueva carga de lote',
                reducedText: 'Nuevo',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes(
                            'Produtos:Carregar lote'
                        )
                    )
                },
                hide: true,
            },
            {
                text: 'Receitas',
                link: '/system/product/receipt',
                description: 'Gestiona las recetas de productos',
                icon: <Receipt />,
                reducedText: 'Receitas',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Receitas:Listar')
                    )
                },
            },
            {
                text: 'Nueva receita',
                link: '/system/product/receipt/new',
                description: 'Registra una nueva receta',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Receitas:Criar')
                    )
                },
                hide: true,
            },
            {
                text: 'Editar receita',
                link: '/system/product/receipt/edit',
                description: 'Edita una receta',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Receitas:Atualizar')
                    )
                },
                hide: true,
            },
            {
                text: 'Fabricaciónes',
                link: '/system/product/phabrication',
                description: 'Gestiona las fabricaciones de productos',
                icon: <Hammer />,
                reducedText: 'Fabricaciónes',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Fabricação:Listar')
                    )
                },
            },
            {
                text: 'Nueva fabricación',
                link: '/system/product/phabrication/new',
                description: 'Registra una nueva fabricación',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Fabricação:Criar')
                    )
                },
                hide: true,
            },
            {
                text: 'Lista categorias',
                link: '/system/product/category',
                icon: <Folder />,
                description: 'Gestiona las categorías de productos',
                reducedText: 'Categorias',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Produtos:Listar')
                    )
                },
            },
            {
                text: 'Edición en massa',
                link: '/system/product/mass-edition',
                icon: <Pencil />,
                description: 'Edita varios productos a la vez',
                reducedText: 'Edición en massa',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes(
                            'Produtos:Edição em massa'
                        )
                    )
                },
            },
        ],
    },
    {
        text: 'Clientes',
        icon: <User />,
        description: 'Gestiona tus clientes',
        validate: (account) => {
            return (
                account.isMaster ||
                !!account.permissions?.some((p) => p.startsWith('Clientes:'))
            )
        },
        subitems: [
            {
                text: 'Lista clientes',
                link: '/system/client',
                icon: <List />,
                description: 'Consulta y busca los clientes registrados',
                reducedText: 'Lista',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Clientes:Listar')
                    )
                },
            },
            {
                text: 'Nuevo cliente',
                link: '/system/client/new',
                icon: <Plus />,
                description: 'Registra un nuevo cliente',
                reducedText: 'Nuevo',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Clientes:Criar')
                    )
                },
            },
            {
                text: 'Editar cliente',
                link: '/system/client/edit',
                description: 'Edita los datos de un cliente',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Clientes:Atualizar')
                    )
                },
                hide: true,
            },
        ],
    },
    {
        text: 'Proveedor',
        icon: <Truck />,
        description: 'Gestiona tus proveedores',
        validate: (account) => {
            return (
                account.isMaster ||
                !!account.permissions?.some((p) =>
                    p.startsWith('Fornecedores:')
                )
            )
        },
        subitems: [
            {
                text: 'Lista proveedores',
                link: '/system/supplier',
                icon: <List />,
                description: 'Consulta y busca los proveedores registrados',
                reducedText: 'Lista',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Fornecedores:Listar')
                    )
                },
            },
            {
                text: 'Nuevo proveedor',
                link: '/system/supplier/new',
                icon: <Plus />,
                description: 'Registra un nuevo proveedor',
                reducedText: 'Nuevo',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Fornecedores:Criar')
                    )
                },
            },
            {
                text: 'Editar proveedor',
                link: '/system/supplier/edit',
                description: 'Edita los datos de un proveedor',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes(
                            'Fornecedores:Atualizar'
                        )
                    )
                },
                hide: true,
            },
        ],
    },
    {
        text: 'Compras',
        icon: <ArrowLeft />,
        description: 'Gestiona las compras de tu empresa',
        validate: (account) => {
            return (
                account.isMaster ||
                !!account.permissions?.some((p) => p.startsWith('Compras:'))
            )
        },
        subitems: [
            {
                text: 'Lista compras',
                link: '/system/buy',
                icon: <List />,
                description: 'Consulta y busca las compras realizadas',
                reducedText: 'Lista',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Compras:Listar')
                    )
                },
            },
            {
                text: 'Nueva compra',
                link: '/system/buy/new',
                icon: <Plus />,
                description: 'Registra una nueva compra',
                reducedText: 'Nueva',
                validate: (account, configs) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Compras:Criar')
                    )
                },
            },
            {
                text: 'Editar compra',
                link: '/system/buy/edit',
                description: 'Edita una compra',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Compras:Atualizar')
                    )
                },
                hide: true,
            },
            {
                text: 'Productos comprados',
                link: '/system/buy/buyed-products',
                icon: <ArrowUp />,
                description: 'Consulta los productos comprados',
                reducedText: 'P. Comprados',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Compras:Dados gerais')
                    )
                },
            },
            {
                text: 'Lista pedidos',
                link: '/system/buy/order',
                icon: <ShoppingBasket />,
                description: 'Gestiona los pedidos de compra',
                reducedText: 'Pedidos',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Compras:Listar')
                    )
                },
            },
            {
                text: 'Nuevo pedido',
                link: '/system/buy/order/new',
                icon: <Plus />,
                description: 'Registra un nuevo pedido de compra',
                reducedText: 'Nuevo pedido',
                validate: (account, configs) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Compras:Criar')
                    )
                },
                hide: true,
            },
            {
                text: 'Editar pedido',
                link: '/system/buy/order/edit',
                description: 'Edita un pedido de compra',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Compras:Atualizar')
                    )
                },
                hide: true,
            },
            {
                text: 'Pagamentos',
                link: '/system/buy/payment-buy',
                icon: <Banknote />,
                description: 'Gestiona los pagos de compras',
                reducedText: 'Pagamentos',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Compras:Listar')
                    )
                },
            },
        ],
    },
    {
        text: 'Ventas',
        icon: <ArrowRight />,
        description: 'Gestiona las ventas de tu empresa',
        validate: (account) => {
            return (
                account.isMaster ||
                !!account.permissions?.some((p) => p.startsWith('Vendas:'))
            )
        },
        subitems: [
            {
                text: 'Lista ventas',
                link: '/system/sale',
                icon: <List />,
                description: 'Consulta y busca las ventas realizadas',
                reducedText: 'Lista',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Vendas:Listar')
                    )
                },
            },
            {
                text: 'Nueva venta',
                link: '/system/sale/new',
                icon: <Plus />,
                description: 'Registra una nueva venta',
                reducedText: 'Nueva',
                validate: (account, configs) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Vendas:Criar')
                    )
                },
            },
            {
                text: 'Editar venta',
                link: '/system/sale/edit',
                description: 'Edita los datos de una venta',
                reducedText: 'Editar',
                validate: (account) => {
                    return true
                },
                hide: true,
            },
            {
                text: 'Productos vendidos',
                link: '/system/sale/sold-products',
                icon: <ArrowUp />,
                description: 'Consulta los productos vendidos',
                reducedText: 'P. Vendidos',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Vendas:Dados gerais')
                    )
                },
            },
            {
                text: 'Orçamentos',
                link: '/system/sale/budget',
                icon: <ClipboardList />,
                description: 'Gestiona los presupuestos de venta',
                reducedText: 'Orçamentos',
                validate: (account, configs) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes(
                            'Vendas:Listar orçamentos'
                        )
                    )
                },
            },
            {
                text: 'Nuevo orçamento',
                link: '/system/sale/budget/new',
                icon: <Plus />,
                description: 'Registra un nuevo presupuesto',
                reducedText: 'Nuevo orçamento',
                validate: (account, configs) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes(
                            'Vendas:Criar orçamento'
                        )
                    )
                },
                hide: true,
            },
            {
                text: 'Editar orçamento',
                link: '/system/sale/budget/edit',
                description: 'Edita un presupuesto',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes(
                            'Vendas:Atualizar orçamento'
                        )
                    )
                },
                hide: true,
            },
            {
                text: 'Pagamentos',
                link: '/system/sale/payment-sale',
                icon: <Banknote />,
                description: 'Gestiona los pagos de ventas',
                reducedText: 'Pagamentos',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Vendas:Listar')
                    )
                },
            },
            {
                text: 'Notas de crédito',
                link: '/system/sale/credit-note',
                icon: <Undo2 />,
                description: 'Gestiona las notas de crédito',
                reducedText: 'Notas de crédito',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes(
                            'Vendas:Nota de crédito'
                        )
                    )
                },
            },
            {
                text: 'Registrar notas de crédito',
                link: '/system/sale/credit-note/new',
                icon: <Plus />,
                description: 'Registra una nueva nota de crédito',
                reducedText: 'Registrar notas de crédito',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes(
                            'Vendas:Nota de crédito'
                        )
                    )
                },
                hide: true,
            },
            {
                text: 'Pagarés',
                link: '/system/sale/contract',
                icon: <Scale />,
                description: 'Gestiona los pagarés y contratos',
                reducedText: 'Pagarés',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Vendas:Listar')
                    )
                },
            },
            {
                text: 'Entregas',
                link: '/system/sale/delivery',
                icon: <Bike />,
                description: 'Gestiona las entregas de ventas',
                reducedText: 'Entregas',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes(
                            'Vendas:Atualizar status entrega'
                        )
                    )
                },
            },
        ],
    },
    {
        text: 'Pagos',
        icon: <CreditCard />,
        description: 'Gestiona los métodos de pago',
        validate: (account) => {
            return (
                account.isMaster ||
                !!account.permissions?.some((p) =>
                    p.startsWith('Métodos de pagamento:')
                )
            )
        },
        subitems: [
            {
                text: 'Lista pagos',
                link: '/system/payment-method',
                icon: <List />,
                description: 'Consulta los métodos de pago registrados',
                reducedText: 'Lista',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes(
                            'Métodos de pagamento:Listar'
                        )
                    )
                },
            },
            {
                text: 'Nuevo pago',
                link: '/system/payment-method/new',
                icon: <Plus />,
                description: 'Registra un nuevo método de pago',
                reducedText: 'Nuevo',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes(
                            'Métodos de pagamento:Criar'
                        )
                    )
                },
            },
            {
                text: 'Editar método de pagamento',
                link: '/system/payment-method/edit',
                description: 'Edita un método de pago',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes(
                            'Métodos de pagamento:Atualizar'
                        )
                    )
                },
                hide: true,
            },
            {
                text: 'Pix',
                link: '/system/payment-method/pix',
                icon: <QrCode />,
                description: 'Gestiona los pagos vía PIX',
                reducedText: 'Pix',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes(
                            'Métodos de pagamento:Gerenciar pagamentos externos'
                        )
                    )
                },
            },
        ],
    },
    {
        text: 'Notas',
        icon: <ReceiptText />,
        description: 'Gestiona las notas fiscales',
        validate: (account) => {
            return (
                account.isMaster ||
                !!account.permissions?.some((p) => p.startsWith('Notas:'))
            )
        },
        subitems: [
            {
                text: 'Lista notas',
                link: '/system/note',
                icon: <List />,
                description: 'Consulta las notas fiscales registradas',
                reducedText: 'Lista',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Notas:Listar')
                    )
                },
            },
            {
                text: 'Nueva nota',
                link: '/system/note/new',
                icon: <Plus />,
                description: 'Registra una nueva nota fiscal',
                reducedText: 'Nueva',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Notas:Criar')
                    )
                },
            },
            {
                text: 'Editar nota',
                link: '/system/note/edit',
                description: 'Edita una nota fiscal',
                reducedText: 'Editar',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Notas:Atualizar')
                    )
                },
                hide: true,
            },
        ],
    },
    {
        text: 'Gastos',
        icon: <ShoppingCart />,
        description: 'Gestiona los gastos de la empresa',
        validate: (account) => {
            return (
                account.isMaster ||
                !!account.permissions?.some((p) => p.startsWith('Gastos:'))
            )
        },
        subitems: [
            {
                text: 'Lista gastos',
                link: '/system/spent',
                icon: <List />,
                description: 'Consulta y busca los gastos realizados',
                reducedText: 'Lista',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Gastos:Listar')
                    )
                },
            },
            {
                text: 'Nuevo gasto',
                link: '/system/spent/new',
                icon: <Plus />,
                description: 'Registra un nuevo gasto',
                reducedText: 'Nuevo',
                validate: (account, configs) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Gastos:Criar')
                    )
                },
            },
            {
                text: 'Editar gasto',
                link: '/system/spent/edit',
                description: 'Edita un gasto',
                reducedText: 'Editar',
                hide: true,
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Gastos:Atualizar')
                    )
                },
            },
        ],
    },
    {
        text: 'Servicios',
        icon: <Wrench />,
        description: 'Gestiona los servicios',
        validate: (account) => {
            return (
                account.isMaster ||
                !!account.permissions?.some((p) => p.startsWith('Serviços:'))
            )
        },
        subitems: [
            {
                text: 'Lista servicios',
                link: '/system/service',
                icon: <List />,
                description: 'Consulta los servicios registrados',
                reducedText: 'Lista',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Serviços:Listar')
                    )
                },
            },
            {
                text: 'Nuevo servicio',
                link: '/system/service/new',
                icon: <Plus />,
                description: 'Registra un nuevo servicio',
                reducedText: 'Nuevo',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Serviços:Criar')
                    )
                },
            },
            {
                text: 'Editar servicio',
                link: '/system/service/edit',
                description: 'Edita un servicio',
                reducedText: 'Editar',
                hide: true,
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Serviços:Atualizar')
                    )
                },
            },
        ],
    },
    {
        text: 'Monedas',
        icon: <ArrowLeftRight />,
        description: 'Gestiona las monedas y cotizaciones',
        validate: (account) => {
            return (
                account.isMaster ||
                !!account.permissions?.some((p) => p.startsWith('Moedas:'))
            )
        },
        subitems: [
            {
                text: 'Lista monedas',
                link: '/system/coin',
                icon: <List />,
                description: 'Consulta las monedas registradas',
                reducedText: 'Lista',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Moedas:Listar')
                    )
                },
            },
            {
                text: 'Nueva moneda',
                link: '/system/coin/new',
                icon: <Plus />,
                description: 'Registra una nueva moneda',
                reducedText: 'Nueva',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Moedas:Criar')
                    )
                },
            },
            {
                text: 'Editar moneda',
                link: '/system/coin/edit',
                description: 'Edita una moneda',
                reducedText: 'Editar',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Moedas:Atualizar')
                    )
                },
                hide: true,
            },
        ],
    },
    {
        text: 'Funcionários',
        icon: <IdCard />,
        description: 'Gestiona los empleados',
        validate: (account) => {
            return (
                account.isMaster ||
                !!account.permissions?.some((p) =>
                    p.startsWith('Funcionários:')
                )
            )
        },
        subitems: [
            {
                text: 'Gerenciar funcionários',
                link: '/system/employee/manage',
                icon: <History />,
                description: 'Administra los empleados de la empresa',
                reducedText: 'Gerenciar',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes(
                            'Funcionários:Gerenciar'
                        )
                    )
                },
            },
            {
                text: 'Lista funcionários',
                link: '/system/employee',
                icon: <List />,
                description: 'Consulta y busca los empleados',
                reducedText: 'Lista',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Funcionários:Listar')
                    )
                },
            },
            {
                text: 'Nuevo funcionário',
                link: '/system/employee/new',
                icon: <Plus />,
                description: 'Registra un nuevo empleado',
                reducedText: 'Nuevo',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Funcionários:Criar')
                    )
                },
            },
            {
                text: 'Editar funcionário',
                link: '/system/employee/edit',
                description: 'Edita los datos de un empleado',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes(
                            'Funcionários:Atualizar'
                        )
                    )
                },
                hide: true,
            },
            {
                text: 'Comissión',
                link: '/system/employee/comission',
                icon: <Banknote />,
                description: 'Gestiona las comisiones de los empleados',
                reducedText: 'Comissión',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Funcionários:Listar')
                    )
                },
            },
            {
                text: 'Pagamientos',
                link: '/system/employee/payment',
                icon: <ClipboardList />,
                description: 'Gestiona los pagos de los empleados',
                reducedText: 'Pagamientos',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes(
                            'Funcionários:Gerenciar'
                        )
                    )
                },
            },
        ],
    },
    {
        text: 'Documentos',
        icon: <FileText />,
        description: 'Gestiona los documentos',
        validate: (account) => {
            return (
                account.isMaster ||
                !!account.permissions?.some((p) => p.startsWith('Documentos:'))
            )
        },
        subitems: [
            {
                text: 'Lista documentos',
                link: '/system/document',
                icon: <List />,
                description: 'Consulta los documentos generados',
                reducedText: 'Lista',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Documentos:Listar')
                    )
                },
            },
        ],
        hide: true,
    },
    {
        text: 'Cuentas',
        icon: <Users />,
        description: 'Gestiona las cuentas de usuarios',
        validate: (account) => {
            return (
                account.isMaster ||
                !!account.permissions?.some((p) => p.startsWith('Contas:'))
            )
        },
        subitems: [
            {
                text: 'Lista cuentas',
                link: '/system/account',
                icon: <List />,
                description: 'Consulta las cuentas de usuarios',
                reducedText: 'Lista',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Contas:Listar')
                    )
                },
            },
            {
                text: 'Nueva cuenta',
                link: '/system/account/new',
                icon: <Plus />,
                description: 'Registra una nueva cuenta de usuario',
                reducedText: 'Nueva',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Contas:Criar')
                    )
                },
            },
            {
                text: 'Editar cuenta',
                link: '/system/account/edit',
                description: 'Edita una cuenta de usuario',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Contas:Atualizar')
                    )
                },
                hide: true,
            },
        ],
    },
    {
        text: 'Caja',
        description: 'Gestiona las cajas',
        icon: <History />,
        validate: (account) => {
            return (
                account.isMaster ||
                !!account.permissions?.some((p) => p.startsWith('Caixas:'))
            )
        },
        subitems: [
            {
                text: 'Administrar cajas',
                link: '/system/cashier/manage',
                icon: <History />,
                description:
                    'Gestiona la apertura, cierre e historial de la caja',
                reducedText: 'Administrar',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes(
                            'Caixas:Gerenciar caixas'
                        )
                    )
                },
            },
            {
                text: 'Movimientos del caja',
                link: '/system/cashier/activity',
                icon: <ArrowUpDown />,
                description: 'Visualiza los movimientos de la caja',
                reducedText: 'Movimientos',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes(
                            'Caixas:Gerenciar caixas'
                        )
                    )
                },
            },
            {
                text: 'Lista cajas',
                link: '/system/cashier',
                icon: <List />,
                description: 'Consulta las cajas registradas',
                reducedText: 'Lista',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Caixas:Listar')
                    )
                },
            },
            {
                text: 'Nuevo caja',
                link: '/system/cashier/new',
                icon: <Plus />,
                description: 'Registra una nueva caja',
                reducedText: 'Nuevo',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Caixas:Criar')
                    )
                },
            },
            {
                text: 'Editar caixa',
                link: '/system/cashier/edit',
                description: 'Edita una caja',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Caixas:Atualizar')
                    )
                },
                hide: true,
            },
        ],
    },
    {
        text: 'Vehículo',
        description: 'Gestiona los vehículos',
        icon: <Car />,
        validate: (account) => {
            return (
                account.isMaster ||
                !!account.permissions?.some((p) => p.startsWith('Veículos:'))
            )
        },
        subitems: [
            {
                text: 'Lista vehículos',
                link: '/system/vehicle',
                icon: <List />,
                description: 'Consulta los vehículos registrados',
                reducedText: 'Lista',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Veículos:Listar')
                    )
                },
            },
            {
                text: 'Nuevo vehículo',
                link: '/system/vehicle/new',
                icon: <Plus />,
                description: 'Registra un nuevo vehículo',
                reducedText: 'Nuevo',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Veículos:Criar')
                    )
                },
            },
            {
                text: 'Editar vehículo',
                link: '/system/vehicle/edit',
                description: 'Edita un vehículo',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Veículos:Atualizar')
                    )
                },
                hide: true,
            },
        ],
    },
    {
        text: 'Painel admin',
        link: '/system/dashboard',
        description: 'Visualiza el panel administrativo',
        icon: <LayoutDashboard />,
        validate: (account) => {
            return (
                account.isMaster ||
                !!account.permissions?.includes('Painel:Visualizar')
            )
        },
        hide: true,
    },
    {
        text: 'Banco',
        description: 'Gestiona bancos y cheques',
        icon: <Landmark />,
        validate: (account) => {
            return (
                account.isMaster ||
                !!account.permissions?.some((p) => p.startsWith('Bancos:'))
            )
        },
        subitems: [
            {
                text: 'Lista bancos',
                link: '/system/bank',
                icon: <List />,
                description: 'Consulta los bancos registrados',
                reducedText: 'Lista',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Bancos:Listar')
                    )
                },
            },
            {
                text: 'Nuevo banco',
                link: '/system/bank/new',
                icon: <Plus />,
                description: 'Registra un nuevo banco',
                reducedText: 'Nuevo',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Bancos:Criar')
                    )
                },
            },
            {
                text: 'Editar banco',
                link: '/system/bank/edit',
                description: 'Edita un banco',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Bancos:Atualizar')
                    )
                },
                hide: true,
            },
            {
                text: 'Lista cheques',
                link: '/system/bank/checks',
                icon: <CheckCircle2 />,
                description: 'Gestiona los cheques',
                reducedText: 'Cheques',
                validate: (account) => {
                    return (
                        account.isMaster ||
                        !!account.permissions?.includes('Bancos:Listar cheques')
                    )
                },
            },
        ],
    },
    {
        text: 'Sucursales',
        icon: <Store />,
        description: 'Gestiona las sucursales',
        validate: (account, configs) => {
            return (
                account.isMaster &&
                configs.systemBranch === SystemBranchEnum.Branch
            )
        },
        subitems: [
            {
                text: 'Lista sucursales',
                link: '/system/branch',
                icon: <List />,
                description: 'Consulta las sucursales registradas',
                reducedText: 'Lista',
                validate: (account, configs) => {
                    return (
                        account.isMaster &&
                        configs.systemBranch === SystemBranchEnum.Branch
                    )
                },
            },
            {
                text: 'Nueva sucursal',
                link: '/system/branch/new',
                icon: <Plus />,
                description: 'Registra una nueva sucursal',
                reducedText: 'Nueva',
                validate: (account, configs) => {
                    return (
                        account.isMaster &&
                        configs.systemBranch === SystemBranchEnum.Branch
                    )
                },
            },
            {
                text: 'Editar sucursal',
                link: '/system/branch/edit',
                description: 'Edita una sucursal',
                validate: (account, configs) => {
                    return (
                        account.isMaster &&
                        configs.systemBranch === SystemBranchEnum.Branch
                    )
                },
                hide: true,
            },
        ],
    },
    {
        text: 'Configs',
        link: '/system/configs',
        description: 'Configuración del sistema',
        icon: <Settings />,
        validate: (account) => {
            return account.isMaster
        },
        hide: true,
    },
    {
        text: 'Render note',
        link: '/system/render-note',
        description: 'Renderizar nota',
        icon: <Settings />,
        validate: (account) => {
            return account.isMaster
        },
        hide: true,
    },
    {
        text: 'Log',
        link: '/system/log',
        description: 'Visualiza los logs del sistema',
        icon: <Settings />,
        validate: (account) => {
            return account.isMaster
        },
        hide: true,
    },
]

function getFlatMenu(): Navigation[] {
    const newNav: Navigation[] = []
    for (const nav of navMenu) {
        newNav.push(nav)
        if (nav.subitems)
            for (const subNav of nav.subitems) {
                newNav.push(subNav)
            }
    }

    return newNav
}

export const flatNavMenu = getFlatMenu()
